import React, { useState, useEffect, useCallback } from 'react'

import Script from 'next/script'

import { useStaticData } from '@mc/static-data-react'
import { track } from '@mc/track-event'
import { isMobile } from '@mc/devices'

import { UserAttributesType } from '../types'
import { setChatbotPosition } from '../helpers'
import { useMoveChatbotButton } from '../hooks'

const INITIALIZE_EVENT = 'DUET_INITIALIZE'
const DEINITIALIZE_EVENT = 'DUET_DEINITIALIZE'

const CHATBOT_CLICK_EVENT_NAME = 'Chatbot Click'
const CHATBOT_SCRIPT_ID = 'chatbot-script-container'

const FIXED_OFFSET_TO_SUBSTRACT = 12

const SCRIPT_URL = 'https://decagon.ai/loaders/masterclass.js'

// Productization note: Move `DecagonAI` component from `web/apps/next/src/pages/help-center/components/DecagonAi.tsx`
// to a different, more accessible place to reuse the same component.
export const CustomerSupportChatbot = ({
  pageSource,
  courseSlug,
  categorySlug,
  subcategorySlug,
}: {
  pageSource: string
  courseSlug?: string
  categorySlug?: string
  subcategorySlug?: string
}) => {
  const { userAttributes } = useStaticData<{
    userAttributes: UserAttributesType
  }>()

  const [chatbotLoaded, setChatbotLoaded] = useState(false)
  const [chatbotModalOpen, setChatbotModalOpen] = useState(false)

  const { moveChatbotButton, offset } = useMoveChatbotButton()

  const trackChatbotInteraction = useCallback(
    (action: 'start' | 'close') => {
      const { href, pathname } = window.location

      track(CHATBOT_CLICK_EVENT_NAME, {
        action,
        page_source: pageSource,
        location: 'Decagon AI Chatbot',
        page_path: pathname,
        course: courseSlug,
        category: categorySlug,
        subcategory: subcategorySlug,
        href,
      })
    },
    [pageSource, courseSlug, categorySlug, subcategorySlug],
  )

  const onDuetChange = useCallback(
    (event: { eventName: string }) => {
      switch (event.eventName) {
        case INITIALIZE_EVENT: {
          setChatbotModalOpen(true)
          trackChatbotInteraction('start')

          break
        }
        case DEINITIALIZE_EVENT: {
          setChatbotModalOpen(false)
          trackChatbotInteraction('close')

          break
        }
        default:
          break
      }
    },
    [trackChatbotInteraction, setChatbotModalOpen],
  )

  const onDuetInit = useCallback(() => {
    if (typeof window?.duet === 'undefined') return

    const {
      device,
      userId,
      country,
      region,
      is_logged_in: isLoggedIn,
      platform_name: platformName,
    } = userAttributes

    const metadata = {
      device,
      region,
      country,
      user_id: userId,
      is_logged_in: isLoggedIn,
      platform_name: platformName,
      page_source: pageSource,
      course: courseSlug,
      category: categorySlug,
      subcategory: subcategorySlug,
    } as Record<string, string | boolean | undefined>

    window.duet.setMetadata(metadata)

    window.duet.setUserId(userId)

    window.duet.setEventListener(onDuetChange)

    window.duet.unhide()

    setChatbotLoaded(true)
  }, [
    setChatbotLoaded,
    onDuetChange,
    userAttributes,
    pageSource,
    courseSlug,
    categorySlug,
    subcategorySlug,
  ])

  useEffect(() => {
    if (chatbotLoaded) {
      if (moveChatbotButton && !chatbotModalOpen) {
        setChatbotPosition({
          bottom: `${offset - FIXED_OFFSET_TO_SUBSTRACT}px`,
        })
      } else {
        setChatbotPosition({ bottom: '0px' })
      }
    }
  }, [moveChatbotButton, chatbotLoaded, chatbotModalOpen, offset])

  useEffect(() => {
    if (window.duet) {
      window.duet.unhide()
    }

    return () => {
      // By default, Next.js Script components persist their loaded scripts across page navigations, causing the chatbot to show in pages
      // where we don't want it to show, so we need to hide the chatbot so users cannot interact with it in pages other than the expected ones.
      if (window.duet) {
        window.duet.hide()
      }
    }
  }, [])

  useEffect(() => {
    if (chatbotModalOpen && isMobile()) {
      document.documentElement.classList.add('mc-overflow--hidden')
    } else {
      document.documentElement.classList.remove('mc-overflow--hidden')
    }
  }, [chatbotModalOpen])

  return (
    <Script
      src={SCRIPT_URL}
      key={pageSource}
      id={CHATBOT_SCRIPT_ID}
      async
      defer
      strategy='lazyOnload'
      onLoad={onDuetInit}
    />
  )
}
